import {
  getAPITaskParamsFromAttempt,
  TaskCreationFailedBannerList,
} from '@github-ui/agent-sessions/components/TaskCreationFailedBanner'
import type {ChatInputRef} from '@github-ui/copilot-chat/components/ChatInput'
import CopilotSuggestions from '@github-ui/copilot-chat/components/CopilotSuggestions'
import {LegalDisclaimer} from '@github-ui/copilot-chat/components/LegalDisclaimer'
import {EMBEDDED_MENU_PORTAL_CLASS, MenuPortalContainer} from '@github-ui/copilot-chat/components/PortalContainerUtils'
import {useQuotaBanner} from '@github-ui/copilot-chat/hooks/use-quota-banner'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import {useSSOOrgNames} from '@github-ui/copilot-chat/hooks/use-sso-org-names'
import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {useChatState} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {type CopilotChatCommandType, ModeSupportName} from '@github-ui/copilot-chat-types'
import CopilotIconAnimation from '@github-ui/copilot-chat-visuals/CopilotIconAnimation'
import {isPaidUser} from '@github-ui/copilot-entitlement/copilot-chat-entitlement'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {AgentsList} from '@github-ui/dashboard-lists/components/AgentsList'
import {sendEvent} from '@github-ui/hydro-analytics'
import {CopilotPromotionalBanner} from '@github-ui/in-product-targeting'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import {useFailedTasks} from '../../contexts/FailedTasksContext'
import {usePersonaOnboarding} from '../../contexts/PersonaOnboardingContext'
import type {HandleMessageSubmit} from '../../hooks/use-handle-message-submit'
import {usePromotionalBanner} from '../../hooks/use-promotional-banner'
import {ChatInputContainer} from '../ChatInputContainer'
import {CopilotEmptyStateAnimation} from '../CopilotEmptyStateAnimation'
import {PersonaOnboardingModal} from '../PersonaOnboarding/PersonaOnboardingModal'
import {PersonaTaskSuggestions} from '../PersonaOnboarding/PersonaTaskSuggestions'
import {CommandStartersAndSuggestions} from './CommandStartersAndSuggestions'
import styles from './NewConversation.module.css'
import {NewConversationBanners} from './NewConversationBanners'
import {SuggestionPills} from './SuggestionPills'

interface NewConversationProps {
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  handleUserSubmit: HandleMessageSubmit
  nextMessageIndex: number
  /** Force icon-only mode in the toolbar, useful for container-based responsive behavior */
  forceIconOnly?: boolean
  /** Whether this is the embedded immersive view (shows URL-based suggestions instead of command starters) */
  isEmbedded?: boolean
}

export function NewConversation({
  textAreaRef,
  handleUserSubmit,
  nextMessageIndex,
  forceIconOnly,
  isEmbedded,
}: NewConversationProps) {
  const state = useChatState()
  const manager = useChatManager()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLFormElement>(null)
  const hasSubmittedChatRef = useRef(false)
  const [hasEmbeddedOverflow, setHasEmbeddedOverflow] = useState(false)
  const promotionalBanner = usePromotionalBanner()
  const personaOnboarding = usePersonaOnboarding()
  const selectedCopilotSpaceId = useSelectedCopilotSpaceId()
  const {needsSsoAuthentication} = useSSOOrgNames()

  const {chatQuotaExceeded, isLicensedLimited, tokenBasedBillingEnabled} = useEntitlement()
  const quotaBanner = useQuotaBanner()
  const isInputDisabled = tokenBasedBillingEnabled
    ? quotaBanner.show && quotaBanner.isBlocking // The input is disabled when users have exceeded their quota (i.e. the banner is in blocking mode)
    : isLicensedLimited && chatQuotaExceeded // The input is disabled only when users have a limited license AND have exceeded their quota
  const [chatCommand, setChatCommand] = useState<CopilotChatCommandType | null>(null)
  const [activeModeName, setActiveModeName] = useState<string>(() => {
    const savedModeName = copilotLocalStorage.getActiveMode()
    return savedModeName ?? ModeSupportName.Ask
  })

  const chatInputRef = useRef<ChatInputRef>(null)
  const commandStartersRef = useCallback(
    (container: HTMLDivElement | null) => () => {
      if (container?.contains(document.activeElement)) textAreaRef.current?.focus()
    },
    [textAreaRef],
  )

  const {plan: copilotPlan} = useEntitlement()

  const isMissionControlEnabled = state.agentsEnabled
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const showProPlusAnimation = useFeatureFlag('copilot_pro_plus_animation')

  const {failedTasks, setFailedTasks, createTask} = useFailedTasks()

  const handleRetry = useCallback(
    (attemptIndex: number) => {
      const attempt = failedTasks[attemptIndex]
      if (attempt) {
        setFailedTasks(prev => prev.filter((_, index) => index !== attemptIndex))
        createTask(getAPITaskParamsFromAttempt(attempt))
      }
    },
    [failedTasks, setFailedTasks, createTask],
  )

  const handleDismiss = useCallback(
    (attemptIndex: number) => {
      setFailedTasks(prev => prev.filter((_, index) => index !== attemptIndex))
    },
    [setFailedTasks],
  )

  const handleSubmit = useCallback<HandleMessageSubmit>(
    (...args) => {
      hasSubmittedChatRef.current = true
      return handleUserSubmit(...args)
    },
    [handleUserSubmit],
  )

  useEffect(() => {
    sendEvent('dotcom_chat.activate', {
      target: 'NEW_CONVERSATION_VIEW_LOADED',
    })

    // Log bounce event if user leaves without submitting a chat
    return () => {
      if (!hasSubmittedChatRef.current) {
        sendEvent('dotcom_chat.activate', {
          target: 'NEW_CONVERSATION_BOUNCE',
          mode: 'immersive',
          bounceToUrl: document.location.href,
        })
      }
    }
  }, [])

  // Regenerate suggestions for embedded mode when component mounts (e.g., new conversation)
  useEffect(() => {
    if (!isEmbedded) return

    const suggestionContext = state.context?.[0] ?? state.currentRepository
    if (suggestionContext) {
      manager.generateSuggestions(suggestionContext)
    }

    return () => {
      manager.clearSuggestions()
    }
  }, [isEmbedded, manager, state.context, state.currentRepository, state.selectedThreadID])

  useEffect(() => {
    if (!isEmbedded) return

    const scrollElement = scrollContainerRef.current
    if (!scrollElement) return

    let frameId = 0

    const updateEmbeddedOverflow = () => {
      if (frameId) cancelAnimationFrame(frameId)

      frameId = requestAnimationFrame(() => {
        const hasOverflow = scrollElement.scrollHeight - scrollElement.clientHeight > 1
        setHasEmbeddedOverflow(prev => (prev === hasOverflow ? prev : hasOverflow))
      })
    }

    updateEmbeddedOverflow()

    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateEmbeddedOverflow)
      resizeObserver.observe(scrollElement)

      const innerContainer = scrollElement.firstElementChild
      if (innerContainer instanceof HTMLElement) {
        resizeObserver.observe(innerContainer)
      }
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId)
      resizeObserver?.disconnect()
    }
  }, [isEmbedded])

  const hasAnyReferences = state.currentReferences.length > 0
  const hideChatInput = needsSsoAuthentication && Boolean(selectedCopilotSpaceId)
  const shouldShowPromotionalBanner = !isEmbedded && !hideChatInput
  const suggestedPersona =
    !isEmbedded &&
    !isInputDisabled &&
    !hideChatInput &&
    !selectedCopilotSpaceId &&
    !hasAnyReferences &&
    !chatCommand &&
    personaOnboarding?.enabled &&
    !personaOnboarding.show &&
    !personaOnboarding.suggestedTasksDismissed
      ? personaOnboarding.persona
      : null

  return (
    <>
      {!isEmbedded && !hideChatInput && !selectedCopilotSpaceId && (
        <PersonaOnboardingModal login={state.currentUserLogin} returnFocusRef={textAreaRef} />
      )}
      <div
        data-testid="new-conversation-scroll-container"
        ref={scrollContainerRef}
        className={clsx(styles.scrollContainer, {
          [styles.embeddedScrollContainer]: isEmbedded,
          [styles.embeddedScrollContainerOverflowing]: isEmbedded && hasEmbeddedOverflow,
        })}
      >
        {!isEmbedded && (
          <MenuPortalContainer className={state.mode === 'assistive' ? EMBEDDED_MENU_PORTAL_CLASS : undefined} />
        )}
        <div className={clsx(styles.container, {[styles.embeddedContainer]: isEmbedded})}>
          {isEmbedded && (
            <div className={styles.embeddedDisclaimer}>
              <LegalDisclaimer />
            </div>
          )}
          <div className={styles.banner}>
            <NewConversationBanners />
          </div>
          <div
            className={clsx(styles.main, {
              [styles.withTaskList]: isMissionControlEnabled && !isEmbedded,
              [styles.embeddedMain]: isEmbedded,
            })}
          >
            <div className={clsx(styles.content, {[styles.embeddedContent]: isEmbedded})}>
              <div className={clsx(styles.innerContainer, {[styles.embeddedInnerContainer]: isEmbedded})}>
                <h1 className="sr-only">Copilot Chat</h1>

                <div
                  className={clsx(styles.inputAndStartersContainer, {
                    [styles.embeddedInputContainer]: isEmbedded,
                  })}
                >
                  {showProPlusAnimation ? <CopilotEmptyStateAnimation /> : <CopilotIconAnimation hidden />}

                  {/* For embedded, show suggestions above input */}
                  {isEmbedded && !isInputDisabled && (
                    <div className={styles.embeddedSuggestions}>
                      <CopilotSuggestions />
                    </div>
                  )}

                  {/* For non-embedded, render the input inside the scroll container */}
                  {!isEmbedded && (
                    <ChatInputContainer
                      chatInputRef={chatInputRef}
                      textAreaRef={textAreaRef}
                      handleUserSubmit={handleSubmit}
                      nextMessageIndex={nextMessageIndex}
                      onChatCommandChange={setChatCommand}
                      isNewConversation
                      containerRef={containerRef}
                      slashCommandsEnabled
                      showDisabledInputOnQuotaExceeded
                      onClickMode={setActiveModeName}
                      activeModeName={activeModeName}
                      forceIconOnly={forceIconOnly}
                    />
                  )}

                  {suggestedPersona && (
                    <PersonaTaskSuggestions
                      key={suggestedPersona}
                      persona={suggestedPersona}
                      returnFocusRef={textAreaRef}
                      onSelect={task => {
                        chatInputRef.current?.setText(task.prompt)
                        textAreaRef.current?.focus()
                      }}
                    />
                  )}

                  {/* For non-embedded, show contextual suggestions when references are attached */}
                  {!isEmbedded && !isInputDisabled && hasAnyReferences && (
                    <div className={styles.commandStartersContainer}>
                      <SuggestionPills
                        chatInputRef={chatInputRef}
                        textAreaRef={textAreaRef}
                        onChatCommandChange={setChatCommand}
                        chatCommand={chatCommand}
                        containerRef={containerRef}
                      />
                    </div>
                  )}

                  {/* For non-embedded, show command starters below input when no references are attached */}
                  {!isEmbedded && !isInputDisabled && !suggestedPersona && !hasAnyReferences && (
                    <div ref={commandStartersRef} className={styles.commandStartersContainer}>
                      <CommandStartersAndSuggestions
                        chatCommand={chatCommand}
                        chatInputRef={chatInputRef}
                        textAreaRef={textAreaRef}
                        containerRef={containerRef}
                        paidUser={isPaidUser(copilotPlan)}
                        activeModeName={activeModeName}
                      />
                    </div>
                  )}

                  {shouldShowPromotionalBanner && promotionalBanner ? (
                    <div className={styles.promotionalBanner}>
                      <CopilotPromotionalBanner banner={promotionalBanner} />
                    </div>
                  ) : null}
                </div>

                <TaskCreationFailedBannerList
                  attempts={failedTasks}
                  onRetry={handleRetry}
                  onDismiss={handleDismiss}
                  className={styles.failedTasksContainer}
                />

                {isMissionControlEnabled && !isEmbedded && (
                  <div className={styles.agentListContainer}>
                    <ConsecutiveCAPI401sProvider>
                      <AgentsList showViewAll title="Recent agent sessions" initialResultCount={5} />
                    </ConsecutiveCAPI401sProvider>
                  </div>
                )}
              </div>
            </div>
          </div>
          {!isEmbedded && (
            <div className={styles.footer}>
              <LegalDisclaimer />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
